// frontend/src/components/layout/CommandPalette.jsx
// REPLACE hardcoded ALL_COMMANDS with Oracle data from Redux
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openTab } from '../../features/tabsSlice';   
import { selectSearchableMenuItems } from "../../features/menuSlice"

const CommandPalette = ({ onClose }) => {
  const dispatch  = useDispatch()
  const [query, setQuery]    = useState("")
  const [selected, setSelected] = useState(0)
  const inputRef  = useRef(null)
  const listRef   = useRef(null)

  // ✅ From Oracle via Redux — not hardcoded
  const menuItems = useSelector(selectSearchableMenuItems)

  // ── Map Oracle row → command ───────────────────────────────
  const allCommands = useMemo(() =>
    menuItems.map(row => ({
      id:         row.FORM_KEY,
      label:      row.MENU_NAME,
      module:     row.MODULE_CODE || "",
      icon:       getCommandIcon(row.MENU_ICON, row.MENU_NAME),
      type:       getCommandType(row.MENU_NAME, row.MODULE_CODE),
      permissions: {
        canView:   row.CAN_VIEW   === "Y",
        canAdd:    row.CAN_ADD    === "Y",
        canEdit:   row.CAN_EDIT   === "Y",
        canDelete: row.CAN_DELETE === "Y",
      },
    }))
  , [menuItems])

  // ── Filter by search query ─────────────────────────────────
  const results = useMemo(() => {
    if (!query.trim()) return allCommands.slice(0, 12)
    const q = query.toLowerCase()
    return allCommands.filter(c =>
      c.label.toLowerCase().includes(q)  ||
      c.module.toLowerCase().includes(q) ||
      c.type.toLowerCase().includes(q)   ||
      c.id?.toLowerCase().includes(q)
    ).slice(0, 15)
  }, [query, allCommands])

  useEffect(() => { inputRef.current?.focus() }, [])
  useEffect(() => { setSelected(0) }, [results.length])

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelected(p => Math.min(p+1, results.length-1)) }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelected(p => Math.max(p-1, 0)) }
      if (e.key === "Enter")     { e.preventDefault(); if (results[selected]) openForm(results[selected]) }
      if (e.key === "Escape")    { onClose() }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [results, selected])

  useEffect(() => {
    listRef.current?.children[selected]?.scrollIntoView({ block:"nearest" })
  }, [selected])

  const openForm = (cmd) => {
    dispatch(openTab({
      id:          cmd.id,
      formType:    cmd.id,
      label:       cmd.label,
      recordId:    null,
      permissions: cmd.permissions,
    }))
    onClose()
  }

  // ── render (same JSX as before) ────────────────────────────
  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-palette" onClick={e => e.stopPropagation()}>
        <div className="cmd-search-row">
          <SearchIc />
          <input
            ref={inputRef}
            className="cmd-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={
              allCommands.length > 0
                ? `Search ${allCommands.length} forms, reports, masters…`
                : "Loading menu from Oracle…"
            }
          />
          <kbd className="cmd-esc" onClick={onClose}>Esc</kbd>
        </div>

        <div className="cmd-results" ref={listRef}>
          {allCommands.length === 0 ? (
            <div className="cmd-empty">
              <div style={{ fontSize:24, marginBottom:8 }}>⏳</div>
              Menu loading from Oracle…
            </div>
          ) : results.length === 0 ? (
            <div className="cmd-empty">
              No results for "<em>{query}</em>"
              <br />
              <small>Check SEPL_MENU_MASTER for FORM_KEY entries</small>
            </div>
          ) : (
            results.map((cmd, i) => (
              <button
                key={cmd.id}
                className={`cmd-item ${i === selected ? "selected" : ""}`}
                onClick={() => openForm(cmd)}
                onMouseEnter={() => setSelected(i)}
              >
                <span className="cmd-item-icon">{cmd.icon}</span>
                <span className="cmd-item-label">{cmd.label}</span>
                <span className="cmd-item-module">{cmd.module}</span>
                <span
                  className="cmd-item-type"
                  style={{ color: TYPE_COLORS[cmd.type] || "#888" }}
                >
                  {cmd.type}
                </span>
                <span className="cmd-item-enter">↵</span>
              </button>
            ))
          )}
        </div>

        <div className="cmd-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
          <span style={{ marginLeft:"auto", opacity:.5 }}>
            {results.length} / {allCommands.length} items
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Helper: map Oracle MENU_ICON string → emoji icon ─────────
const getCommandIcon = (iconKey, label = "") => {
  const MAP = {
    cart:      "🛒", bag:       "📦", ledger:    "📒",
    rupee:     "💰", gst:       "🏛", chart:     "📊",
    truck:     "🚚", box:       "📦", warehouse: "🏭",
    hr:        "👤", gear:      "⚙",  users:     "👥",
    grid:      "📊", check:     "✅", default:   "📄",
  }
  if (MAP[iconKey]) return MAP[iconKey]
  const n = label.toLowerCase()
  if (n.includes("invoice"))  return "🧾"
  if (n.includes("report"))   return "📋"
  if (n.includes("master"))   return "👤"
  if (n.includes("salary"))   return "💰"
  if (n.includes("gst"))      return "🏛"
  if (n.includes("balance"))  return "📋"
  return "📄"
}

// ── Helper: derive command type from name ─────────────────────
const getCommandType = (name = "", module = "") => {
  const n = name.toLowerCase()
  if (n.includes("master"))  return "master"
  if (n.includes("report") || n.includes("register") ||
      n.includes("balance") || n.includes("ledger") ||
      n.includes("summary") || n.includes("analysis")) return "report"
  if (module === "SYS")      return "admin"
  return "form"
}

const TYPE_COLORS = {
  form:   "#185FA5",
  master: "#1D9E75",
  report: "#534AB7",
  admin:  "#5F5E5A",
}

const SearchIc = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
       style={{ color:"var(--color-text-tertiary)", flexShrink:0 }}>
    <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
)

export default CommandPalette